import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { api } from '../utils/api';
import { CheckCircle2, XCircle, Sparkles, Loader2, RefreshCw } from 'lucide-react';

const MCQAgentDrafts = () => {
  const [drafts, setDrafts] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [rowBusy, setRowBusy] = useState(null);
  const [genLevel, setGenLevel] = useState('P5');
  const [genCount, setGenCount] = useState(5);
  const [generating, setGenerating] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.adminListDrafts();
      setDrafts(data.drafts || []);
      setCounts(data.counts_by_type_level || {});
    } catch (e) {
      toast.error('Failed to load drafts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleGenerate = async () => {
    setGenerating(true);
    const toastId = toast.loading(`Generating ${genCount} ${genLevel} drafts (this can take ~30–60s)…`);
    try {
      const res = await api.adminMcqAgentGenerate({
        level: genLevel,
        count: genCount,
        question_types: ['grammar_mcq', 'vocabulary_mcq'],
      });
      toast.success(res.message, { id: toastId });
      await refresh();
    } catch (e) {
      const msg = e.response?.data?.detail || 'MCQ agent generation failed';
      toast.error(typeof msg === 'string' ? msg : 'MCQ agent generation failed', { id: toastId });
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (id) => {
    setRowBusy(id);
    try {
      await api.adminApproveDraft(id);
      toast.success('Draft approved & published');
      setDrafts((prev) => prev.filter((d) => d.id !== id));
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to approve';
      toast.error(typeof msg === 'string' ? msg : 'Failed to approve');
    } finally {
      setRowBusy(null);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this draft? It will be permanently removed.')) return;
    setRowBusy(id);
    try {
      await api.adminRejectDraft(id);
      toast.success('Draft rejected');
      setDrafts((prev) => prev.filter((d) => d.id !== id));
    } catch (e) {
      toast.error('Failed to reject');
    } finally {
      setRowBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="mcq-drafts-root">
      {/* Header + generate controls */}
      <Card className="p-6 rounded-3xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full mb-2">
              <Sparkles size={10} /> MCQ Agent
            </div>
            <h2 className="text-xl font-heading font-black text-slate-900">Question drafts awaiting review</h2>
            <p className="text-sm text-slate-600 mt-1">
              The MCQ Agent generates drafts every <strong>Monday &amp; Thursday at 09:00 SGT</strong> from a 1,996-word Oxford
              taxonomy. Drafts are invisible to students until you approve them here.
            </p>
          </div>
          <Button
            onClick={refresh}
            variant="outline"
            size="sm"
            className="rounded-full"
            data-testid="mcq-drafts-refresh"
          >
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </div>

        <div className="flex flex-wrap items-end gap-3 pt-3 border-t border-emerald-200">
          <div>
            <label className="block text-[11px] font-black uppercase tracking-wide text-slate-500 mb-1">Level</label>
            <select
              value={genLevel}
              onChange={(e) => setGenLevel(e.target.value)}
              className="border-2 border-slate-200 rounded-xl px-3 py-2 text-sm font-bold"
              data-testid="mcq-gen-level"
            >
              <option value="P4">P4</option>
              <option value="P5">P5</option>
              <option value="P6">P6</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-black uppercase tracking-wide text-slate-500 mb-1">Count</label>
            <select
              value={genCount}
              onChange={(e) => setGenCount(parseInt(e.target.value, 10))}
              className="border-2 border-slate-200 rounded-xl px-3 py-2 text-sm font-bold"
              data-testid="mcq-gen-count"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
            </select>
          </div>
          <Button
            onClick={handleGenerate}
            disabled={generating}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-full px-5"
            data-testid="mcq-gen-btn"
          >
            {generating ? (
              <><Loader2 size={14} className="animate-spin mr-2" /> Generating…</>
            ) : (
              <><Sparkles size={14} className="mr-2" /> Generate now</>
            )}
          </Button>
          {Object.keys(counts).length > 0 && (
            <div className="text-xs text-slate-600 ml-auto">
              <strong>{drafts.length}</strong> pending ·{' '}
              {Object.entries(counts).map(([k, v]) => `${k}: ${v}`).join(' · ')}
            </div>
          )}
        </div>
      </Card>

      {/* Drafts list */}
      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="animate-spin text-emerald-600" size={28} />
        </div>
      ) : drafts.length === 0 ? (
        <Card className="p-10 rounded-3xl border-2 border-dashed border-slate-300 text-center">
          <Sparkles className="mx-auto mb-3 text-slate-400" size={32} />
          <div className="font-bold text-slate-700">No drafts pending</div>
          <p className="text-sm text-slate-500 mt-1">
            The MCQ Agent will drop fresh drafts here every Monday &amp; Thursday. Or click{' '}
            <em>Generate now</em> above to trigger a run.
          </p>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="mcq-drafts-list">
          {drafts.map((d) => (
            <motion.div
              key={d.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border-2 border-slate-200 rounded-2xl p-5"
              data-testid={`mcq-draft-${d.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <span className="bg-emerald-100 text-emerald-800 font-black px-2 py-0.5 rounded-full">
                    {d.question_type}
                  </span>
                  <span className="bg-slate-100 text-slate-800 font-black px-2 py-0.5 rounded-full">
                    {d.level}
                  </span>
                  {d.topic && (
                    <span className="bg-amber-100 text-amber-800 font-bold px-2 py-0.5 rounded-full">
                      {d.topic}
                    </span>
                  )}
                  {d.subtopic && (
                    <span className="bg-slate-50 text-slate-600 font-semibold px-2 py-0.5 rounded-full border border-slate-200">
                      {d.subtopic}
                    </span>
                  )}
                  <span className="text-slate-400 text-[10px]">
                    {d.source} · {new Date(d.created_at).toLocaleString('en-SG', { dateStyle: 'medium', timeStyle: 'short' })}
                  </span>
                </div>
              </div>

              <div className="text-slate-900 font-semibold mb-2 whitespace-pre-wrap">
                {d.question_text}
              </div>

              {Array.isArray(d.options) && d.options.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-3">
                  {d.options.map((opt, i) => {
                    const letter = ['A', 'B', 'C', 'D'][i];
                    const isCorrect = opt === d.correct_answer;
                    return (
                      <div
                        key={i}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
                          isCorrect
                            ? 'border-2 border-emerald-500 bg-emerald-50 text-emerald-900'
                            : 'border-slate-200 bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span className="font-black mr-2">{letter}.</span>
                        {opt}
                        {isCorrect && <CheckCircle2 size={13} className="inline ml-1.5 text-emerald-600" />}
                      </div>
                    );
                  })}
                </div>
              )}

              {d.explanation && (
                <div className="text-xs text-slate-600 leading-relaxed bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3">
                  <strong className="text-slate-800">Explanation:</strong> {d.explanation}
                </div>
              )}

              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => handleApprove(d.id)}
                  disabled={rowBusy === d.id}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full font-bold"
                  data-testid={`mcq-approve-${d.id}`}
                >
                  {rowBusy === d.id ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <>
                      <CheckCircle2 size={13} className="mr-1" /> Approve &amp; publish
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleReject(d.id)}
                  disabled={rowBusy === d.id}
                  variant="outline"
                  size="sm"
                  className="rounded-full font-bold text-rose-600 border-rose-300 hover:bg-rose-50"
                  data-testid={`mcq-reject-${d.id}`}
                >
                  <XCircle size={13} className="mr-1" /> Reject
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MCQAgentDrafts;
